(ns ui.core
  "The 'machinery' ('functional core imperative shell') that allows to keep most of the
   other UI implementation free of side effects (pure functions) to foster the
   benefits of functional programming.


   Please read:

   - https://replicant.fun/top-down/

   - https://replicant.fun/tutorials/network/"
  (:require [replicant.dom :as r]
            [parts.replicant.router :as router]
            [app.client-log :as client-log]
            [app.bulk.unsaved-guard :as bulk-unsaved-guard]
            [ui.editor2.text :as text]
            [ui.ui :as ui]
            [ui.modal]
            [ui.error-alert]
            [ui.upload-status]
            [ui.register]
            [parts.replicant.core]
            [ui.notify :as notify]
            [ui.upload-manager :as upload-manager]
            [datascript.transit]
            ))

(defonce el
  (js/document.getElementById "app"))

(defn get-render-f [{:keys [ui/state] :as w}]
  (:render
   (router/get-page-by-id w
                          (-> state :location :location/page-id))
   ))

(defn render-el!
  "Renders hiccup into el via replicant, remounting el when the render throws.

   Browser extensions like password managers insert their own DOM nodes into
   elements replicant manages (e.g. an icon overlay next to a login input).
   Replicant addresses children by index, so a foreign node can make
   reconciliation throw (\"Failed to execute 'replaceChild' on 'Node'\").
   The exception leaves replicant's internal :rendering? flag for el stuck at
   true, after which replicant queues every render forever and the UI freezes
   (observed on /ui/connect3 with LastPass). Dropping replicant's bookkeeping
   for el makes the retry start from a blank element, which re-establishes a
   DOM that matches the vdom."
  [el hiccup opts]
  (try
    (r/render el hiccup opts)
    (catch :default e
      (js/console.error "Replicant render failed, remounting" e)
      (client-log/log! :ui/render-remounted {:error (str e)})
      ;; The remount replaces the whole DOM tree, which would silently drop
      ;; the focus (and thereby subsequent keystrokes) from the input the
      ;; user is typing in. Restore focus and cursor via the element id.
      (let [active (.-activeElement js/document)
            active-id (when active (.-id active))
            selection-start (when active (.-selectionStart active))
            selection-end (when active (.-selectionEnd active))]
        (vswap! r/state dissoc el)
        (r/render el hiccup opts)
        (when (seq active-id)
          (when-let [refocus-el (js/document.getElementById active-id)]
            (.focus refocus-el)
            (when (and (some? selection-start)
                       (some? selection-end))
              (try
                (.setSelectionRange refocus-el selection-start selection-end)
                (catch :default _e nil)))))))))

(defn render! [{:keys [ui/state ui/routes] :as w}]
  (let [f (or (get-render-f w)
              ui/render-page)]
    (render-el! el
                [:div
                 (ui.error-alert/alert state)
                 (ui.upload-status/live-region state)
                 (ui.upload-status/render state)
                 (ui.modal/render state)
                 (f state)]
                {:alias-data {:routes routes}})))

(defn add-render
  [w]
  (assoc w
         :ui/render!
         #'render!))

(defonce system
  (atom nil))

(defn get-store
  []
  (:ui/store @system))

(defn add-get-register
  [w]
  (assoc w
         :ui/get-register
         (fn []
           ui.register/register)))

(defn add-render-watcher!
  "Renders on store changes, debounced to one requestAnimationFrame per burst
   of updates, always rendering the latest store state.

   Used instead of parts.replicant.core/add-render-watcher!, which renders
   synchronously inside the atom watch. A synchronous render can re-enter
   replicant.dom/render while a render is already in progress, e.g. when a
   password-manager extension reacts to replicant setting an input value by
   dispatching a synthetic input event, whose action handler swaps the store.
   Replicant then queues the in-flight hiccup and replays it on the next
   animation frame. That replay is stale by the time it runs, which resets
   input values to old state and can end in an endless render loop that
   corrupts form input (observed on /ui/connect3 with LastPass)."
  [{:keys [ui/store] :as w}]
  (let [render-queued? (volatile! false)]
    (add-watch store
               :ui/render-watcher
               (fn [_ _ old-state new-state]
                 (when (and (not= old-state new-state)
                            (not @render-queued?))
                   (vreset! render-queued? true)
                   (js/requestAnimationFrame
                     (fn [_]
                       (vreset! render-queued? false)
                       ((:ui/render! w) (assoc w :ui/state @store))))))))
  w)

(defn add-resize-render-trigger
  "When the user resized the window, we need trigger replicant render.
   Assocs the resize time so the store value actually changes, since
   add-render-watcher! skips renders when the state is unchanged."
  [w]
  (.addEventListener js/window
                     "resize"
                     (fn [_e]
                       (swap! (:ui/store w)
                              assoc
                              :ui/viewport-resized-at
                              (js/Date.now))))
  w
  )

(defn add-audio-player
  "Global audio player for handling playback."
  [w]
  (let [audio (js/Audio.)]
    (.setAttribute audio "style" "display: none;")
    (.appendChild js/document.body audio)
    (assoc w
           :audio-player
           audio)))

(defn add-audio-player-ended-listener
  "When the audio finishes playing, update the store so that the UI will show the play icon."
  [w]
  (.addEventListener (:audio-player w)
                     "ended"
                     (fn []
                       (swap! (:ui/store w) dissoc :audio/current-url)))
  w)

(defn add-editor2-text-editor-watch!
  "Watches the sidebar panel state and initializes the
   prosemirror text editor when all conditions meet."
  [{:keys [ui/store] :as w}]
  (add-watch
    store
    ::init-sidebar-prosemirror
    (fn [_ _ _ state]
      (when (get-in state
                    [:editor2 :init-text-editor?])
        ;; The same swap that sets :init-text-editor? also opens the sidebar
        ;; panel containing the #prosemirror-editor element, and since
        ;; add-render-watcher! renders on the next animation frame, that
        ;; element does not exist yet. Defer to a requestAnimationFrame:
        ;; browsers run the callbacks in registration order, and the render
        ;; watcher registered its frame first (its watch was added earlier),
        ;; so the element is in the DOM by the time this callback runs. The
        ;; re-check makes multiple swaps within one frame init only once,
        ;; since init-prosemirror-editor! resets the flag.
        (js/requestAnimationFrame
          (fn [_]
            (when (get-in @store
                          [:editor2 :init-text-editor?])
              (swap! store
                     text/init-prosemirror-editor!)))))))
  w)

(defn add-db-view-notify-listener!
  [{:keys [ui/store] :as w}]
  (notify/start-listening! store)
  w)

(defn add-transit-opts
  [w]
  (assoc w
         :transit/read-opts
         {:handlers datascript.transit/read-handlers}
         :transit/write-opts
         {:handlers datascript.transit/write-handlers}))

(defn common-init-system
  "Initializes the Replicant UI parts that are used for all Replicant pages
   and Reagent Replicant modals."
  [w]
  (-> w
      (add-transit-opts)
      (add-audio-player)
      (add-audio-player-ended-listener)
      (parts.replicant.core/add-ui-log)
      (parts.replicant.core/add-store)
      (add-get-register)
      (parts.replicant.core/add-pages)
      (parts.replicant.core/add-routes)
      (add-render)
      (add-render-watcher!)
      (parts.replicant.core/add-event-handler)
      (parts.replicant.core/add-dispatch!)
      (parts.replicant.core/add-store-dispatch!)
      (parts.replicant.core/add-routing-anchor!)
      (add-resize-render-trigger)
      ))

(defn v2-init-system
  "Initializes additional UI parts required for Replicant pages."
  [w]
  (-> w
      (bulk-unsaved-guard/add-guards!)
      (parts.replicant.core/add-route-click!)
      (parts.replicant.core/add-navigate!)
      (add-editor2-text-editor-watch!)
      (upload-manager/add-driver!)
      (add-db-view-notify-listener!)
      ))

(defn common-init!
  []
  (when-not @system
    (let [w (common-init-system {:ui/command-endpoint "/command2"})]
      (reset! system
              w)
      (swap! (:ui/store w)
             assoc
             :app/started-at (js/Date.)
             :ui/active-url js/location.pathname
             )))
  )

(defonce ^:private app-loaded-logged?
  (atom false))

(defn main []
  (common-init!)
  (swap! system
         v2-init-system)

  ;; Funnel step "App Loaded": the legacy Reagent SPA emitted this since
  ;; forever, but the Replicant pages never did, so the funnel dashboard
  ;; undercounted app loads. The atom keeps dev-mode hot reloads (main is
  ;; the :dev/after-load hook) from emitting again.
  (when-not @app-loaded-logged?
    (reset! app-loaded-logged? true)
    (client-log/log! :app/loaded))

  ((:ui/event-handler @system)
   {}
   [[:auth/check]])

  ;; Trigger the initial render
  (router/navigate! @system)
  )
